import { lazy, Suspense, useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import DayWithWithout from "@/components/landing/DayWithWithout";
import HowItWorks from "@/components/landing/HowItWorks";
import ReadingProgress from "@/components/landing/ReadingProgress";
import DeferredRender from "@/components/DeferredRender";

// Below-the-fold sections lazy-loaded to shrink the initial JS payload.
const ProductShowcase = lazy(() => import("@/components/landing/ProductShowcase"));
// PricingSection removed, invite-only, no public pricing surface.
// PatientVoices removed, invite-only pre-launch, no verified customer quotes yet.
const FAQ = lazy(() => import("@/components/landing/FAQ"));
const Commitments = lazy(() => import("@/components/landing/Commitments"));
const FinalCTA = lazy(() => import("@/components/landing/FinalCTA"));
const Footer = lazy(() => import("@/components/landing/Footer"));
const CheckinSimulator = lazy(() => import("@/components/marketing/CheckinSimulator"));
const InstrumentSection = lazy(() => import("@/components/landing/InstrumentSection"));
const ByTheNumbers = lazy(() => import("@/components/landing/ByTheNumbers"));

import { getSoftwareApplicationSchema, getOrganizationSchema } from "@/lib/structuredData";
import { captureUtmIfPresent } from "@/lib/funnelAnalytics";
import { trackHomepageVisit } from "@/lib/siteTracking";
import { prefetchOnIdle } from "@/lib/prefetchRoute";
import { useScrollDepth } from "@/lib/scrollTracking";
import ScrollSection from "@/components/analytics/ScrollSection";
import { useCanonical } from "@/lib/useCanonical";
import { ANALYTICS_CONSENT_EVENT } from "@/lib/analyticsConsent";

const Index = () => {
  useCanonical({ path: "/" });
  useScrollDepth("landing");
  useEffect(() => {
    captureUtmIfPresent();
    trackHomepageVisit();
    const handleAnalyticsConsent = (event: Event) => {
      if ((event as CustomEvent).detail === "accepted") {
        captureUtmIfPresent();
        trackHomepageVisit();
      }
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, handleAnalyticsConsent);
    // Warm the most-clicked next routes while the browser is idle.
    prefetchOnIdle([
      "/demo",
      "/blog",
      "/signup",
      "/login",
      "/waitlist",
      "/for-clinicians",
      "/energy-gap",
      "/who-we-are",
      "/product",
    ]);

    const existingApp = document.getElementById("app-jsonld");
    if (existingApp) existingApp.remove();
    const appScript = document.createElement("script");
    appScript.id = "app-jsonld";
    appScript.type = "application/ld+json";
    appScript.textContent = JSON.stringify(getSoftwareApplicationSchema());
    document.head.appendChild(appScript);

    const existingOrg = document.getElementById("org-jsonld");
    if (existingOrg) existingOrg.remove();
    const orgScript = document.createElement("script");
    orgScript.id = "org-jsonld";
    orgScript.type = "application/ld+json";
    orgScript.textContent = JSON.stringify(getOrganizationSchema());
    document.head.appendChild(orgScript);

    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_EVENT, handleAnalyticsConsent);
      document.getElementById("app-jsonld")?.remove();
      document.getElementById("org-jsonld")?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReadingProgress />
      <Navbar />
      <main>
        <ScrollSection page="landing" name="hero" as="div"><Hero /></ScrollSection>
        <ScrollSection page="landing" name="day_with_without" as="div"><DayWithWithout /></ScrollSection>
        <DeferredRender id="instrument" minHeight="640px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="landing" name="energy_instrument" as="div"><InstrumentSection /></ScrollSection>
          </Suspense>
        </DeferredRender>
        <ScrollSection page="landing" name="how_it_works" as="div"><HowItWorks /></ScrollSection>
        <DeferredRender minHeight="220px">
          <Suspense fallback={null}>
            <ScrollSection page="landing" name="by_the_numbers" as="div"><ByTheNumbers /></ScrollSection>
          </Suspense>
        </DeferredRender>
        <DeferredRender id="try-checkin" minHeight="720px">
          <ScrollSection
            page="landing"
            name="checkin_simulator"
            aria-label="Try a check-in"
            className="container mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28"
          >
            <div className="scroll-mt-24">
              <Suspense fallback={null}>
                <CheckinSimulator variant="inline" />
              </Suspense>
            </div>
          </ScrollSection>
        </DeferredRender>
        <DeferredRender id="features" minHeight="1600px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="landing" name="product_showcase" as="div"><ProductShowcase /></ScrollSection>
            <ScrollSection page="landing" name="commitments" as="div"><Commitments /></ScrollSection>
          </Suspense>
        </DeferredRender>
        <DeferredRender minHeight="900px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="landing" name="faq" as="div"><FAQ /></ScrollSection>
          </Suspense>
        </DeferredRender>

        <DeferredRender id="final-cta" minHeight="720px">
          <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
            <ScrollSection page="landing" name="final_cta" as="div"><FinalCTA /></ScrollSection>
          </Suspense>
        </DeferredRender>
      </main>
      <DeferredRender minHeight="480px">
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </DeferredRender>
      {/* RequestAccessDialog mounted globally in App.tsx */}

    </div>
  );
};

export default Index;